import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@carbon/react';
import { Close, Launch } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer/drawer';
import { PriceCurvePreviewChart } from 'components/price-curve-chart/price-curve-preview-chart';
import type { PriceCurveSeries } from 'components/price-curve-chart/types';
import moment from 'moment';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import request from 'lib/request';
import { PriceDeckScheduleTable } from 'pages/price-decks/price-deck-schedule-table';
import type {
  PriceDeckDetail,
  PriceDeckPreviewResponse,
} from 'pages/price-decks/types';
import { calculationCreatedAtAtom, appliedConfigAtom } from './atoms';

interface PriceDeckShowResponse {
  data: PriceDeckDetail;
}
import style from './price-deck-detail-panel.module.scss';

interface Props {
  packageId: number;
  priceDeckId: number | null;
  open: boolean;
  onClose: () => void;
}

const PriceDeckDetailPanel: FC<Props> = ({
  packageId,
  priceDeckId,
  open,
  onClose,
}) => {
  const calcCreatedAt = useAtomValue(calculationCreatedAtAtom(packageId));
  const appliedConfig = useAtomValue(appliedConfigAtom(packageId));

  // Only use historical view when viewing the same deck that was used for the calculation.
  // If the user changed the dropdown to a different deck, show the current version.
  const asOfDate =
    appliedConfig?.priceDeckId === priceDeckId && calcCreatedAt
      ? calcCreatedAt
      : null;

  // Fetch deck details via createResourceAtom
  const deckHref =
    open && priceDeckId
      ? `/price_decks/${priceDeckId}${
          asOfDate ? `?as_of=${encodeURIComponent(asOfDate)}` : ''
        }`
      : undefined;
  const deckResource = useAtomValue(
    useMemo(
      () => createResourceAtom<PriceDeckShowResponse>(deckHref),
      [deckHref]
    )
  );
  const { loading: deckLoading, data: deckData } = formatResource(deckResource);
  const deck = deckData?.data ?? null;

  // Fetch preview when deck loads
  const [preview, setPreview] = useState<PriceDeckPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!deck) {
      setPreview(null);
      return;
    }

    if (previewAbortRef.current) previewAbortRef.current.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    setPreviewLoading(true);

    const cfg = deck.config;
    request
      .post<PriceDeckPreviewResponse, unknown>(
        '/price_decks/preview',
        {
          priceDeck: {
            methodType: deck.methodType,
            stripDateMode: deck.stripDateMode,
            stripPriceDate: deck.stripPriceDate,
            stripSource: cfg.strip_source,
            oilFlatPrice: cfg.oil_flat_price,
            gasFlatPrice: cfg.gas_flat_price,
            oilScaleFactor: cfg.oil_scale_factor,
            oilFallbackType: cfg.oil_fallback_type,
            oilStripDuration: cfg.oil_strip_duration,
            gasStripDuration: cfg.gas_strip_duration,
            gasScaleFactor: cfg.gas_scale_factor,
            gasFallbackType: cfg.gas_fallback_type,
            blobPath: cfg.blob_path,
            originalFilename: cfg.original_filename,
            deckType: cfg.deck_type,
            anchorDate: cfg.anchor_date,
          },
        },
        controller
      )
      .then((res) => {
        if (res.type === 'success') setPreview(res.data);
      })
      .catch(() => {
        // Aborted — ignore
      })
      .finally(() => setPreviewLoading(false));

    return () => controller.abort();
  }, [deck]);

  const loading = deckLoading || previewLoading;

  const handleOpenNewTab = useCallback(() => {
    if (priceDeckId) {
      const asOfParam = calcCreatedAt
        ? `?as_of=${encodeURIComponent(calcCreatedAt)}`
        : '';
      window.open(
        `/price_decks/${priceDeckId}${asOfParam}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [priceDeckId, calcCreatedAt]);

  const isStripMethod =
    deck?.methodType === 'strip' || deck?.methodType === 'modified_strip';
  const todayTs = moment.utc().startOf('month').valueOf();

  const { chartSeries, chartDates } = useMemo<{
    chartSeries: PriceCurveSeries | null;
    chartDates: number[];
  }>(() => {
    if (!preview?.rows?.length) return { chartSeries: null, chartDates: [] };
    const dates = preview.rows.map((r) => moment.utc(r.month).valueOf());
    return {
      chartSeries: {
        oil: preview.rows.map(
          (r, i) => [dates[i], r.oilPrice] as [number, number]
        ),
        gas: preview.rows.map(
          (r, i) => [dates[i], r.gasPrice] as [number, number]
        ),
      },
      chartDates: dates,
    };
  }, [preview]);

  const forwardRows = useMemo(
    () =>
      preview?.rows.filter(
        (r) => r.month >= moment.utc().format('YYYY-MM-01')
      ) ?? [],
    [preview]
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={700}
      className={style.drawer}
    >
      <DrawerHeader className={style.header}>
        <span className={style.title}>
          {deck ? `Price Deck - ${deck.name}` : 'Price Deck'}
        </span>
        <div className={style.headerActions}>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Launch}
            iconDescription="Open in new tab"
            hasIconOnly
            onClick={handleOpenNewTab}
          />
          <Button
            kind="ghost"
            size="sm"
            renderIcon={(props) => <Close size={20} {...props} />}
            iconDescription="Close"
            hasIconOnly
            onClick={onClose}
          />
        </div>
      </DrawerHeader>
      <DrawerContent className={style.content}>
        {loading && <p>Loading...</p>}

        {!loading && preview && (
          <>
            {chartSeries && (
              <section className={style.section}>
                <h4 className={style.sectionTitle}>Price Curve Preview</h4>
                <PriceCurvePreviewChart
                  series={chartSeries}
                  dates={chartDates}
                  filterMode="projection"
                  todayLineTs={isStripMethod ? todayTs : undefined}
                />
              </section>
            )}

            {preview.rows.length > 0 && (
              <section className={style.section}>
                <h4 className={style.sectionTitle}>Price Schedule</h4>
                <PriceDeckScheduleTable rows={forwardRows} />
              </section>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export { PriceDeckDetailPanel };
